"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { House, Mail, Menu, Newspaper, SquareCode, User, X } from "lucide-react";
import Link from "next/link";
import LanguageDropdown from "./LanguageDropdown";
import type { LucideIcon } from "lucide-react";
import Image from "next/image";

export default function Navbar() {

    const pathname = usePathname();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const mobileMenuRef = useRef<HTMLDivElement | null>(null);

    const navItems: { label: string; icon: LucideIcon; href: string }[] = [
        {
            label: "Accueil",
            icon: House,
            href: "/",
        },
        {
            label: "À propos",
            icon: User,
            href: "/a-propos",
        },
        {
            label: "Projets",
            icon: SquareCode,
            href: "/projets",
        },
        {
            label: "Blog",
            icon: Newspaper,
            href: "/blog",
        },
        {
            label: "Contact",
            icon: Mail,
            href: "/contact",
        },
    ];

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [pathname]);

    useEffect(() => {
        if (!isMobileMenuOpen) return;

        document.body.style.overflow = "hidden";

        const onMouseDown = (e: MouseEvent) => {
            const wrap = mobileMenuRef.current;
            if (!wrap) return;
            if (e.target instanceof Node && !wrap.contains(e.target)) setIsMobileMenuOpen(false);
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsMobileMenuOpen(false);
        };

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = "auto";
        };
    }, [isMobileMenuOpen]);

    return (
        <div>
            {/* desktop navbar */}
            <nav className="fixed left-0 top-0 h-full w-20 bg-background/80 backdrop-blur-2xl border-r border-white/15 z-40 hidden lg:flex flex-col justify-between items-center py-6">
                <div className="flex flex-col items-center gap-8">
                    <Logo />
                    <ul className="flex flex-col gap-2">
                        {
                            navItems.map((item, index) => {
                                return (
                                    <li key={index} className="group relative">
                                        <Link href={item.href} className={`
                                    block p-3 rounded-xl transition-all duration-500 ease-[0.23,1,0.32,1] group 
                                    ${pathname === item.href
                                                ? "bg-white/30 text-white"
                                                : "text-white/70 opacity-60 hover:opacity-100 hover:text-white transition-all duration-500 ease-[0.23,1,0.32,1] hover:bg-white/30"
                                            }`
                                        }>
                                            <item.icon className="size-4" />
                                        </Link>
                                        {/* <div
                                    className="
                                        absolute left-14 group-hover:left-12 top-1/2 -translate-y-1/2 ml-3 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out
                                        bg-surface-container-high text-white text-sm px-3 py-1 rounded-lg
                                        before:content-[''] before:absolute before:top-1/2 before:-translate-y-1/2 before:-left-[9px]
                                        before:w-0 before:h-0
                                        before:border-t-[5px] before:border-b-[5px] before:border-r-[9px] before:border-l-0
                                        before:border-t-transparent before:border-b-transparent before:border-r-surface-container-high
                                    "
                                >
                                    <span>{item.label}</span>
                                </div> */}
                                    </li>
                                );
                            })
                        }
                    </ul>
                </div>
                {/* <LanguageDropdown placement="right-bottom" /> */}
            </nav>
            {/* mobile navbar */}
            <nav className={`${isMobileMenuOpen ? "border-transparent" : "border-white/15"} border-b flex lg:hidden items-center justify-between fixed top-0 left-0 w-full h-20 px-4 bg-background/80 backdrop-blur-2xl z-40`}>
                <Logo />
                <div className="flex items-center gap-3">

                    <button
                        type="button"
                        className="flex items-center justify-center aspect-square size-11 rounded-full hover:bg-white/5 transition-colors duration-200 ease-in-out"
                        aria-haspopup="menu"
                        aria-expanded={isMobileMenuOpen}
                        aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                        onClick={() => setIsMobileMenuOpen((v) => !v)}
                    >
                        {isMobileMenuOpen ? <X className="size-7" /> : <Menu className="size-7" />}
                    </button>
                </div>
            </nav>

            {isMobileMenuOpen && (
                <div className="lg:hidden fixed inset-0 z-30">
                    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
                    <div
                        ref={mobileMenuRef}
                        className="absolute top-19 left-0 right-0 bg-background/80 backdrop-blur-2xl border border-outline-variant/10 rounded-b-2xl p-3 shadow-[0_0_50px_rgba(0,0,0,0.35)]"
                        role="menu"
                        aria-label="Mobile navigation"
                    >
                        <ul className="space-y-2">
                            {navItems.map((item) => {
                                const isActive = pathname === item.href;
                                return (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-colors group
                                                ${isActive
                                                    ? "bg-white/30 text-white"
                                                    : "bg-transparent hover:bg-white/30 text-white/70 hover:text-white"
                                                }`}
                                        >
                                            <span className="flex items-center gap-3">
                                                <item.icon
                                                    className={
                                                        isActive
                                                            ? "size-5 text-white"
                                                            : "size-5 text-white/70 group-hover:text-white"
                                                    }
                                                />
                                                <span className="font-label">{item.label}</span>
                                            </span>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    )
}

function Logo() {
    return (
        <Image
            src="/images/profile.jpeg"
            alt="Logo Benjamin Drouin"
            width={40}
            height={40}
            priority
            className="rounded-full aspect-square size-10 object-cover"
        />
    );
}